import * as React from "react";
import {Fragment, useCallback, useState} from "react";
import {
    classNames,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    layout,
    PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    typography,
    useAsync,
    useAsyncCallback,
    View,
    ViewContent
} from "@variocube/cube-ui";
import {Controller} from "../controller/Controller";
import {stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";
import {unit} from "../controller/unit";
import {useCurrentEventValue} from "../EventSource";

interface SystemViewProps {
    controller: Controller;
}

export function SystemView({controller}: SystemViewProps) {
    const {t} = useLocalization();

    const restartController = useAsyncCallback(async () => controller.restartController(), [controller]);
    const restartUserInterface = useAsyncCallback(async () => controller.restartUserInterface(), [controller]);
    const restartOperatingSystem = useAsyncCallback(async () => controller.restartOperatingSystem(), [controller]);
    const updateSoftware = useAsyncCallback(async () => controller.updateSoftware(), [controller]);
    const shutdown = useAsyncCallback(async () => controller.shutdown(), [controller]);

    const devices = useAsync(() => controller.getDevices(), [controller]);

    const unitStatus = useCurrentEventValue(unit.status);

    return (
        <View>
            <ViewContent className={layout.horizontal}>
                <div className={layout.fill}>
                    <h2 className={classNames(typography.title, layout.paddingSmall)}>{t("Devices")}</h2>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("Vendor")}</TableCell>
                                <TableCell>{t("Model")}</TableCell>
                                <TableCell>{t("Type")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {devices.result?.map(device => (
                                <TableRow key={device.id}>
                                    <TableCell>{device.vendor}</TableCell>
                                    <TableCell>{device.model}</TableCell>
                                    <TableCell>{device.types.join(", ")}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <h2 className={classNames(typography.title, layout.paddingSmall)}>{t("Packages")}</h2>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("Package")}</TableCell>
                                <TableCell>{t("Version")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(unitStatus?.packages ?? {})
                                .filter(([_, packageInfo]) => packageInfo.installed)
                                .map(([packageName, packageInfo]) => (
                                <TableRow key={packageName}>
                                    <TableCell>{packageName}</TableCell>
                                    <TableCell>{packageInfo.installed}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                </div>
                <div className={styles.buttonBar}>
                    <ConfirmButton
                        title={t("RestartController")}
                        message={t("RestartControllerMessage")}
                        onConfirm={restartController.execute}
                        disabled={restartController.loading}
                    />
                    <ConfirmButton
                        title={t("RestartUserInterface")}
                        message={t("RestartUserInterfaceMessage")}
                        onConfirm={restartUserInterface.execute}
                        disabled={restartUserInterface.loading}
                    />
                    <ConfirmButton
                        title={t("RestartOperatingSystem")}
                        message={t("RestartOperatingSystemMessage")}
                        onConfirm={restartOperatingSystem.execute}
                        disabled={restartOperatingSystem.loading}
                    />
                    <ConfirmButton
                        title={t("UpdateSoftware")}
                        message={t("UpdateSoftwareMessage")}
                        onConfirm={updateSoftware.execute}
                        disabled={updateSoftware.loading}
                    />
                    <ConfirmButton
                        title={t("Shutdown")}
                        message={t("ShutdownMessage")}
                        onConfirm={shutdown.execute}
                        disabled={shutdown.loading}
                    />
                </div>
            </ViewContent>
        </View>
    );
}

interface ConfirmButtonProps {
    title: string;
    message: string;
    disabled?: boolean;
    onConfirm: () => any;
}

function ConfirmButton({title, message, disabled, onConfirm}: ConfirmButtonProps) {
    const [open, setOpen] = useState(false);
    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);
    const {t} = useLocalization();
    return (
        <Fragment>
            <PushButton className={styles.button} onClick={handleOpen} disabled={disabled}>
                {title}
            </PushButton>
            <Dialog open={open} onBackdropClick={handleClose}>
                <DialogHeader title={title}/>
                <DialogContent>
                    {message}
                </DialogContent>
                <DialogFooter>
                    <PushButton primary onClick={onConfirm}>{title}</PushButton>
                    {" "}
                    <PushButton onClick={handleClose}>{t("Cancel")}</PushButton>
                </DialogFooter>
            </Dialog>
        </Fragment>
    )
}

const styles = stylesheet({
    buttonBar: {
        flex: 0,
        backgroundColor: "#eee",
        padding: "0.5rem"
    },
    button: {
        margin: "0.5rem 0",
        width: "100%",
    }
});